import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import useTerrainSettings from '../../hooks/useTerrainSettings';
import useDoiSettings from '../../hooks/useDoiSettings';
import { makeDoiMask } from '../../lib/inversion-results';
import { terrainApi } from '../../lib/terrain-api';
import { getTileAuthToken, rasterTileUrl, startTileAuth } from '../../lib/raster-api';
import { BASEMAP_TILE_SOURCES } from '../../lib/map-styles';
import { run } from '../../lib/worker-pool';
import {
  DEFAULT_GRID, drapedFenceGeometry, elevationAt, drapedVoxelPositions, gridLonLats, gridSpec, rangeTiles, sceneExtent, tileRangeFor, tileUrl,
} from '../../lib/terrain-mesh';

/**
 * 3D terrain draping (docs/DEM_TERRAIN.md, section 6). Rendered inside the viewer's
 * <Canvas> through SubsurfaceViewer3D's `sceneExtras({ origin })`.
 *
 * When the project has a DEM (lib/terrain-settings.js): samples the DEM on an N x N grid
 * over the scene extent (POST /dem/sample, spacing = grid cell), builds the mesh in the
 * geo worker (task `terrainMesh`), drapes the hillshade or elevation tiles as a texture,
 * and redraws the fences and voxel clouds hung from the ground (DEM, or the model's own
 * surface when it has topography). Everything sits in one group whose y-scale is the
 * vertical exaggeration. `renderDraped({ groundY, refElev })` draws extra nodes inside that
 * group (boreholes hung at their collar elevation); `fallback` is drawn instead while
 * there is no terrain. `onReady(bool)` tells the page whether the draped versions are
 * drawn, so it can hide the flat ones (SubsurfaceViewer3D `terrainDraped`).
 */

const STOPS = [[37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [251, 146, 60], [248, 113, 113]];

function rampColor(t, out, k) {
  const p = Math.max(0, Math.min(1, t)) * (STOPS.length - 1);
  const lo = Math.floor(p), hi = Math.min(STOPS.length - 1, lo + 1), fr = p - lo;
  for (let i = 0; i < 3; i++) out[k + i] = STOPS[lo][i] + (STOPS[hi][i] - STOPS[lo][i]) * fr;
}

function logRange(cells) {
  let lo = Infinity, hi = -Infinity;
  cells.forEach((c) => {
    if (c.resistivity > 0) {
      const v = Math.log10(c.resistivity);
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  });
  return [lo, hi > lo ? hi - lo : 1];
}

/** Stations x depth-levels colour texture (same ramp and DOI mask as the flat FencePlane). */
function fenceTexture(entry, doi) {
  const cells = entry.cells || [];
  const xs = [...new Set(cells.map((c) => c.x))].sort((a, b) => a - b);
  const ds = [...new Set(cells.map((c) => c.depth))].sort((a, b) => a - b);
  const w = Math.max(2, xs.length), h = Math.max(2, ds.length);
  const xi = new Map(xs.map((x, i) => [x, i]));
  const di = new Map(ds.map((d, i) => [d, i]));
  const [tMin, span] = logRange(cells);
  const belowDoi = makeDoiMask(cells, { mask: doi.mask, threshold: doi.threshold, fallbackDoi: entry.model?.doi });
  const data = new Uint8Array(w * h * 4);
  cells.forEach((c) => {
    const ix = xi.get(c.x), iy = di.get(c.depth);
    if (ix === undefined || iy === undefined || !(c.resistivity > 0) || belowDoi(c)) return;
    const idx = ((h - 1 - iy) * w + ix) * 4;
    rampColor((Math.log10(c.resistivity) - tMin) / span, data, idx);
    data[idx + 3] = 255;
  });
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  tex.needsUpdate = true;
  tex.magFilter = THREE.NearestFilter;
  return tex;
}

function DrapedFence({ entry, grid, origin, refElev, opacity, clippingPlanes }) {
  const doi = useDoiSettings();
  const built = useMemo(() => {
    const g = drapedFenceGeometry(entry, grid, origin, refElev);
    if (!g) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(g.positions, 3));
    geom.setAttribute('uv', new THREE.BufferAttribute(g.uvs, 2));
    geom.setIndex(new THREE.BufferAttribute(g.indices, 1));
    return { geom, tex: fenceTexture(entry, { mask: doi.mask, threshold: doi.threshold }) };
  }, [entry, grid, origin, refElev, doi.mask, doi.threshold]);
  useEffect(() => () => { built?.geom.dispose(); built?.tex.dispose(); }, [built]);
  if (!built) return null;
  return (
    <mesh geometry={built.geom}>
      <meshBasicMaterial map={built.tex} side={THREE.DoubleSide} transparent opacity={opacity} clippingPlanes={clippingPlanes} />
    </mesh>
  );
}

function DrapedVoxels({ entry, grid, origin, refElev, opacity, clippingPlanes }) {
  const doi = useDoiSettings();
  const geom = useMemo(() => {
    const belowDoi = makeDoiMask(entry.cells || [], { mask: doi.mask, threshold: doi.threshold, fallbackDoi: entry.model?.doi });
    const cells = (entry.cells || []).filter((c) => c.lon !== undefined && c.resistivity > 0 && !belowDoi(c));
    const [tMin, span] = logRange(cells);
    const col = new Float32Array(cells.length * 3);
    cells.forEach((c, i) => {
      rampColor((Math.log10(c.resistivity) - tMin) / span, col, i * 3);
      for (let k = 0; k < 3; k++) col[i * 3 + k] /= 255;
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(drapedVoxelPositions(cells, grid, origin, refElev), 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return g;
  }, [entry, grid, origin, refElev, doi.mask, doi.threshold]);
  useEffect(() => () => geom.dispose(), [geom]);
  return (
    <points geometry={geom}>
      <pointsMaterial size={0.8} vertexColors transparent opacity={opacity} sizeAttenuation clippingPlanes={clippingPlanes} />
    </points>
  );
}

/** Fetch the tiles of a range and compose them on a canvas. DEM tiles need the bearer token; basemap tiles are plain CORS fetches (a failed tile stays blank). */
async function composeTexture(template, range, { token = null } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = 256 * range.nx;
  canvas.height = 256 * range.ny;
  const ctx = canvas.getContext('2d');
  await Promise.all(rangeTiles(range).map(async (t) => {
    try {
      const res = await fetch(tileUrl(template, t), token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      if (!res.ok) return;
      const bmp = await createImageBitmap(await res.blob());
      ctx.drawImage(bmp, t.col * 256, t.row * 256);
    } catch { /* leave the tile blank */ }
  }));
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

async function demToken() {
  const { supabase } = await import('../../lib/supabase');
  await startTileAuth(supabase);
  return getTileAuthToken();
}

const FLAT_GRID = 16;

export default function TerrainMesh({ origin, models = [], opacity = 0.95, clippingPlanes = [], showModels = true, onReady,
  fallback = null, renderDraped = null }) {
  const terrain = useTerrainSettings();
  const basemap = BASEMAP_TILE_SOURCES[terrain.drapeStyle] || null;
  const demId = terrain.drape3d ? terrain.demDatasetId : null;
  // a flat ground image (no DEM) is drawn only when a basemap texture is chosen
  const flat = terrain.drape3d && !terrain.demDatasetId && !!basemap;
  const kind = basemap ? 'basemap' : 'dem';
  const version = terrain.dem?.job_id || '';
  const placed = useMemo(() => models.filter((m) => (m.line?.length === 2 && m.cells?.length)
    || m.cells?.[0]?.lon !== undefined), [models]);
  const extent = useMemo(() => ((demId || flat) && origin ? sceneExtent(placed) : null), [demId, flat, origin, placed]);
  const [mesh, setMesh] = useState(null);
  const [texture, setTexture] = useState(null);

  useEffect(() => {
    if (!extent || !(demId || flat)) { setMesh(null); return undefined; }
    let active = true;
    // the uvs use the tile range of the texture kind, so the mesh is rebuilt when the kind changes
    const tiles = tileRangeFor(extent, kind);
    const build = (elevations, n, refElev) => run('terrainMesh', { elevations, n, extent, origin, tiles, refElev }, { label: 'Terrain mesh' })
      .then((m) => { if (active) setMesh(m ? { ...m, n, extent, tiles, flat, kind } : null); });
    let job;
    if (flat) {
      job = build(new Array(FLAT_GRID * FLAT_GRID).fill(0), FLAT_GRID, 0);
    } else {
      const { n, spacing } = gridSpec(extent, DEFAULT_GRID);
      job = terrainApi.sample(demId, gridLonLats(extent, n), spacing).then((r) => build(r.data.elevations, n));
    }
    job.catch(() => { if (active) setMesh(null); });
    return () => { active = false; };
  }, [extent, demId, flat, kind, origin]);

  useEffect(() => {
    if (!mesh || mesh.kind !== (basemap ? 'basemap' : 'dem')) { setTexture(null); return undefined; }
    let active = true;
    let made = null;
    const compose = basemap
      ? composeTexture(basemap.template, mesh.tiles)
      : demToken().then((token) => composeTexture(
        rasterTileUrl(demId, { style: terrain.drapeStyle === 'elevation' ? 'elevation' : 'hillshade', v: version }), mesh.tiles, { token }));
    compose
      .then((tex) => { made = tex; if (active) setTexture(tex); else tex.dispose(); })
      .catch(() => { if (active) setTexture(null); });
    return () => { active = false; if (made) made.dispose(); };
  }, [mesh, demId, basemap, terrain.drapeStyle, version]);

  const geometry = useMemo(() => {
    if (!mesh) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(mesh.uvs, 2));
    g.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    return g;
  }, [mesh]);
  useEffect(() => () => geometry?.dispose(), [geometry]);

  const ready = !!geometry && !mesh?.flat;
  useEffect(() => { if (onReady) onReady(ready); }, [ready, onReady]);
  useEffect(() => () => { if (onReady) onReady(false); }, [onReady]);

  const { grid, groundY } = useMemo(() => {
    if (!mesh) return {};
    const g0 = { values: mesh.filled, n: mesh.n, extent: mesh.extent };
    // y of a ground point in this group (before VE): the collar elevation when known, else the DEM
    const gy = (lon, lat, collar) => {
      const g = Number.isFinite(collar) ? collar : elevationAt(g0, lon, lat);
      return g === null || g === undefined ? 0 : g - mesh.refElev;
    };
    return { grid: g0, groundY: gy };
  }, [mesh]);

  if (!geometry) return fallback;
  if (mesh.flat) {
    return (
      <>
        <group scale={[1, Math.max(0.1, Number(terrain.ve3d) || 1), 1]} name="ground-image">
          <mesh geometry={geometry}>
            <meshBasicMaterial key={texture ? 'tex' : 'flat'} map={texture || null} color={texture ? '#ffffff' : '#8b9a7a'} side={THREE.DoubleSide}
              transparent opacity={terrain.surfaceOpacity3d} />
          </mesh>
        </group>
        {fallback}
      </>
    );
  }
  const fences = !showModels ? [] : placed.filter((m) => m.line?.length === 2 && !['grid', 'mesh3d'].includes(m.model?.geometryKind));
  const voxels = !showModels ? [] : placed.filter((m) => ['grid', 'mesh3d'].includes(m.model?.geometryKind));
  return (
    <group scale={[1, Math.max(0.1, Number(terrain.ve3d) || 1), 1]} name="terrain">
      <mesh geometry={geometry} receiveShadow>
        <meshLambertMaterial key={texture ? 'tex' : 'flat'} map={texture || null} color={texture ? '#ffffff' : '#8b9a7a'}
          side={THREE.DoubleSide} transparent opacity={terrain.surfaceOpacity3d} />
      </mesh>
      {fences.map((f) => (
        <DrapedFence key={`dfence-${f.id}`} entry={f} grid={grid} origin={origin} refElev={mesh.refElev}
          opacity={opacity} clippingPlanes={clippingPlanes} />
      ))}
      {voxels.map((f) => (
        <DrapedVoxels key={`dvox-${f.id}`} entry={f} grid={grid} origin={origin} refElev={mesh.refElev}
          opacity={opacity} clippingPlanes={clippingPlanes} />
      ))}
      {renderDraped && renderDraped({ groundY, refElev: mesh.refElev })}
    </group>
  );
}
